/**
 * Offline mirror model for a single mail account (pure, DOM-free, serializable).
 *
 * The web mail client reads folders/messages/threads/details from the server's
 * per-account `MailCache` over REST. That keeps the network cache in TanStack
 * Query, which is **in-memory only** — a reload with no server (offline, on a
 * plane, flaky link) starts empty. This module is the persistent local espejo:
 * a snapshot of what the account has fetched, so reading mail, opening a thread
 * and searching keep working offline and reconcile on reconnect.
 *
 * It is deliberately a plain immutable data model with pure transforms so it is
 * trivially unit-testable and storage-agnostic (see `mirror-store.ts` for the
 * IndexedDB/memory persistence and `mirror-service.ts` for the live wiring).
 */

import type {
  MailFolder,
  MailThread,
  MessageDetail,
  MessageSummary,
  SendMessageInput,
} from "./api";

/** Bumped whenever the on-disk shape changes; older blobs are discarded. */
export const MIRROR_VERSION = 1;

export type OutboxKind = "draft" | "send";

/** A compose op (send/draft) queued while offline, replayed on reconnect. */
export interface OutboxEntry {
  id: string;
  kind: OutboxKind;
  input: SendMessageInput;
  queuedAt: string;
}

/** Persistent per-account snapshot of the mailbox. */
export interface MailboxMirror {
  version: number;
  accountId: string;
  folders: MailFolder[];
  /** Message summaries keyed by folder name. */
  messagesByFolder: Record<string, MessageSummary[]>;
  /** Full message details keyed by `folder uid`. */
  details: Record<string, MessageDetail>;
  /** Thread listings keyed by folder name. */
  threadsByFolder: Record<string, MailThread[]>;
  /** Compose ops queued while offline. */
  outbox: OutboxEntry[];
  updatedAt: string;
}

const DETAIL_SEP = " ";
const detailKey = (folder: string, uid: number): string => `${folder}${DETAIL_SEP}${uid}`;

/** A fresh, empty mirror for an account. */
export function emptyMirror(accountId: string): MailboxMirror {
  return {
    version: MIRROR_VERSION,
    accountId,
    folders: [],
    messagesByFolder: {},
    details: {},
    threadsByFolder: {},
    outbox: [],
    updatedAt: new Date(0).toISOString(),
  };
}

// ─── Transforms (immutable — each returns a new mirror) ──────────────────────

export function putFolders(m: MailboxMirror, list: MailFolder[]): MailboxMirror {
  return { ...m, folders: [...list] };
}

export function putMessages(
  m: MailboxMirror,
  folder: string,
  list: MessageSummary[],
): MailboxMirror {
  return { ...m, messagesByFolder: { ...m.messagesByFolder, [folder]: [...list] } };
}

export function putThreads(m: MailboxMirror, folder: string, list: MailThread[]): MailboxMirror {
  return { ...m, threadsByFolder: { ...m.threadsByFolder, [folder]: [...list] } };
}

export function putDetail(m: MailboxMirror, message: MessageDetail): MailboxMirror {
  return { ...m, details: { ...m.details, [detailKey(message.folder, message.uid)]: message } };
}

/** Patches the seen/flagged state of a message across summaries, threads and detail. */
export function applyFlags(
  m: MailboxMirror,
  folder: string,
  uid: number,
  patch: { seen?: boolean; flagged?: boolean },
): MailboxMirror {
  const patchSummary = (s: MessageSummary): MessageSummary =>
    s.uid === uid ? { ...s, seen: patch.seen ?? s.seen, flagged: patch.flagged ?? s.flagged } : s;

  const messagesByFolder = { ...m.messagesByFolder };
  const folderList = messagesByFolder[folder];
  if (folderList) {
    messagesByFolder[folder] = folderList.map(patchSummary);
  }

  const threadsByFolder = { ...m.threadsByFolder };
  const folderThreads = threadsByFolder[folder];
  if (folderThreads) {
    threadsByFolder[folder] = folderThreads.map((thread) => ({
      ...thread,
      messages: thread.messages.map(patchSummary),
    }));
  }

  const details = { ...m.details };
  const key = detailKey(folder, uid);
  const existing = details[key];
  if (existing) {
    details[key] = {
      ...existing,
      seen: patch.seen ?? existing.seen,
      flagged: patch.flagged ?? existing.flagged,
    };
  }

  return { ...m, messagesByFolder, threadsByFolder, details };
}

/** Removes a message from a folder (move/expunge) so offline views stay coherent. */
export function removeMessage(m: MailboxMirror, folder: string, uid: number): MailboxMirror {
  const messagesByFolder = { ...m.messagesByFolder };
  const folderList = messagesByFolder[folder];
  if (folderList) {
    messagesByFolder[folder] = folderList.filter((s) => s.uid !== uid);
  }
  const threadsByFolder = { ...m.threadsByFolder };
  const folderThreads = threadsByFolder[folder];
  if (folderThreads) {
    threadsByFolder[folder] = folderThreads
      .map((thread) => ({ ...thread, messages: thread.messages.filter((s) => s.uid !== uid) }))
      .filter((thread) => thread.messages.length > 0);
  }
  const removedKey = detailKey(folder, uid);
  const details = Object.fromEntries(
    Object.entries(m.details).filter(([key]) => key !== removedKey),
  );
  return { ...m, messagesByFolder, threadsByFolder, details };
}

// ─── Outbox ──────────────────────────────────────────────────────────────────

export function enqueueOutbox(m: MailboxMirror, entry: OutboxEntry): MailboxMirror {
  return { ...m, outbox: [...m.outbox, entry] };
}

export function dequeueOutbox(m: MailboxMirror, id: string): MailboxMirror {
  return { ...m, outbox: m.outbox.filter((e) => e.id !== id) };
}

// ─── Read accessors (safe defaults) ──────────────────────────────────────────

export function folders(m: MailboxMirror): MailFolder[] {
  return m.folders;
}

export function messages(m: MailboxMirror, folder: string): MessageSummary[] {
  return m.messagesByFolder[folder] ?? [];
}

export function threads(m: MailboxMirror, folder: string): MailThread[] {
  return m.threadsByFolder[folder] ?? [];
}

export function detail(m: MailboxMirror, folder: string, uid: number): MessageDetail | undefined {
  return m.details[detailKey(folder, uid)];
}

export function outboxEntries(m: MailboxMirror): OutboxEntry[] {
  return m.outbox;
}

/**
 * Offline full-text-ish search across every mirrored folder. Matches a
 * case-insensitive substring against subject/from/to/snippet — the same fields
 * the server's offline search index covers — de-duped by folder+uid and sorted
 * newest-first so it mirrors the online result ordering.
 */
export function searchMirror(m: MailboxMirror, query: string): MessageSummary[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [];
  }
  const seen = new Set<string>();
  const hits: MessageSummary[] = [];
  for (const [folder, list] of Object.entries(m.messagesByFolder)) {
    for (const s of list) {
      const key = detailKey(folder, s.uid);
      if (seen.has(key)) {
        continue;
      }
      const haystack = `${s.subject} ${s.from} ${s.to.join(" ")} ${s.snippet}`.toLowerCase();
      if (haystack.includes(q)) {
        seen.add(key);
        hits.push(s);
      }
    }
  }
  return hits.toSorted((a, b) => b.date.localeCompare(a.date));
}

// ─── Serialization ───────────────────────────────────────────────────────────

export function serialize(m: MailboxMirror): string {
  return JSON.stringify({ ...m, version: MIRROR_VERSION });
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * Parses a persisted blob back into a mirror, rejecting anything whose version
 * or shape does not match the current model (returns `null` so the caller falls
 * back to a fresh mirror rather than trusting corrupt/stale data).
 */
export function deserialize(raw: string): MailboxMirror | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    !isRecord(parsed) ||
    parsed.version !== MIRROR_VERSION ||
    typeof parsed.accountId !== "string"
  ) {
    return null;
  }
  const base = emptyMirror(parsed.accountId);
  // Trust-boundary read of a self-written, version-gated blob: element shapes
  // are our own serialized types. One narrowing assertion, then guarded reads.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const obj = parsed as Partial<MailboxMirror>;
  return {
    ...base,
    folders: Array.isArray(obj.folders) ? obj.folders : [],
    messagesByFolder:
      obj.messagesByFolder && typeof obj.messagesByFolder === "object" ? obj.messagesByFolder : {},
    details: obj.details && typeof obj.details === "object" ? obj.details : {},
    threadsByFolder:
      obj.threadsByFolder && typeof obj.threadsByFolder === "object" ? obj.threadsByFolder : {},
    outbox: Array.isArray(obj.outbox) ? obj.outbox : [],
    updatedAt: typeof obj.updatedAt === "string" ? obj.updatedAt : base.updatedAt,
  };
}
